import { getDefaultGlobalCta, getPageCta, resolveMarketingPageId } from '@content';
import useAnimationPreferences from '@hooks/use-animation-preferences';
import { useLocale } from '@hooks/use-locale';
import { cn } from '@lib/theme';
import { Link, useLocation } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { useMemo } from 'react';

export function CTASection() {
  const location = useLocation();
  const { locale, localizeHref } = useLocale();
  const { shouldAnimate } = useAnimationPreferences();
  const pageId = resolveMarketingPageId(location.pathname);
  const content = pageId ? getPageCta(pageId, locale) : getDefaultGlobalCta(locale);
  const ctaUrl = localizeHref(content?.ctaUrl ?? '/contact');
  const isInternalLink = useMemo(() => !/^https?:/i.test(ctaUrl), [ctaUrl]);
  const isTamil = locale === 'ta';
  const linkClassName = cn(
    'zari-hover group inline-flex min-h-14 items-center gap-4 rounded-full bg-brand-zari px-7 text-sm font-bold text-accent-foreground',
    'transition-[background-color,transform] duration-300 hover:bg-[#efc96f] focus-visible:ring-2 focus-visible:ring-[#f4d78f] focus-visible:ring-offset-4 focus-visible:ring-offset-[#090a17]'
  );
  const linkChildren = (
    <>
      {content.ctaText}
      <span className="bg-accent-foreground flex size-8 items-center justify-center rounded-full text-[#f5d783]">
        <ArrowUpRight
          className="size-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
          aria-hidden="true"
        />
      </span>
    </>
  );

  const action = isInternalLink ? (
    <Link to={ctaUrl} className={linkClassName}>
      {linkChildren}
    </Link>
  ) : (
    <a href={ctaUrl} className={linkClassName} target="_blank" rel="noreferrer">
      {linkChildren}
    </a>
  );

  return (
    <section className="relative isolate min-h-[72svh] overflow-hidden bg-[#090a17] text-white" aria-label="Next step">
      <img
        src="/assets/optimized/zari-thread-cinematic.webp"
        alt="Gold zari thread travelling across midnight handloom silk"
        className="absolute inset-0 size-full object-cover object-[62%_center] opacity-80"
        loading="lazy"
        decoding="async"
      />
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(9,10,23,0.98)_0%,rgba(9,10,23,0.82)_44%,rgba(9,10,23,0.24)_100%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(9,10,23,0.28),transparent_35%,rgba(9,10,23,0.7))]" />

      <svg
        className="text-brand-zari/55 absolute inset-x-0 bottom-[18%] h-28 w-full"
        viewBox="0 0 1440 120"
        fill="none"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <motion.path
          d="M-40 88C220 12 370 132 640 58C850 1 1115 110 1480 22"
          stroke="currentColor"
          strokeWidth="1.4"
          initial={false}
          whileInView={shouldAnimate ? { pathLength: [0, 1] } : undefined}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
        />
      </svg>

      <div className="relative mx-auto flex min-h-[72svh] max-w-7xl flex-col justify-between px-5 py-16 sm:px-8 sm:py-20 lg:px-12 lg:py-24">
        <div className="flex items-center gap-4 text-[0.68rem] font-bold tracking-[0.2em] text-[#e4b95b] uppercase">
          <span className="h-px w-12 bg-current" aria-hidden="true" />
          {isTamil ? 'ஒரு நூலில் தொடங்குகிறது' : 'It begins with one thread'}
        </div>

        <div className="max-w-4xl py-16">
          <span className="bg-brand-zari/70 mb-7 block h-px w-24" aria-hidden="true" />
          <h2 className="font-heading max-w-4xl text-[clamp(3rem,7vw,6rem)] leading-[0.96] font-medium tracking-[-0.04em]">
            {content.title || 'Heritage woven into every thread'}
          </h2>
          <p className="mt-7 max-w-2xl text-base leading-8 text-[#ddd7ca] sm:text-lg">{content.subtitle}</p>
          <motion.div className="mt-10 inline-flex">{action}</motion.div>
        </div>

        <div className="border-brand-zari/30 flex flex-wrap items-center justify-between gap-5 border-t pt-6 text-xs text-[#c5bdad]">
          <span>{isTamil ? 'எலம்பிள்ளை · 1965 முதல்' : 'Elampillai · Since 1965'}</span>
          <span className="tracking-[0.18em] uppercase">
            {isTamil ? 'உங்கள் கதை · எங்கள் கைவினை' : 'Your story · our craft'}
          </span>
        </div>
      </div>
    </section>
  );
}
